import { useMemo, useState } from 'react';
import { Avatar, Badge, Button, Group, Select, Stack, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../../api/client';
import type { PersonList } from '../../api/types';
import { useSession } from '../../app/session';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { formatDate } from '../../lib/format';
import { PersonFormDrawer } from './PersonFormDrawer';

export function StaffListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organisation, can, enumChoices } = useSession();
  const org = organisation!.slug;

  const [query, setQuery] = useState('');
  const [debounced] = useDebouncedValue(query, 250);
  const [status, setStatus] = useState<string | null>(null);
  const [kind, setKind] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [isFormOpen, setFormOpen] = useState(false);

  const people = useQuery({
    queryKey: ['people', debounced, status, kind, page],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/people/', {
          params: {
            query: {
              search: debounced || undefined,
              pilot_status: status || undefined,
              staff_kind: kind || undefined,
              is_active: true,
              page,
              page_size: 50,
              ordering: 'last_name,first_name',
            },
          },
        }),
      ),
  });

  const columns = useMemo<ColumnDef<PersonList, unknown>[]>(
    () => [
      {
        id: 'name',
        header: t('common.name'),
        cell: ({ row }) => (
          <Group gap="sm" wrap="nowrap">
            <Avatar src={row.original.photo} size={30} radius="xl">
              {initials(row.original.full_name)}
            </Avatar>
            <Stack gap={0}>
              <Text fz="sm" fw={600}>
                {row.original.full_name}
              </Text>
              {row.original.email ? (
                <Text fz="xs" c="dimmed">
                  {row.original.email}
                </Text>
              ) : null}
            </Stack>
          </Group>
        ),
      },
      {
        accessorKey: 'pilot_status',
        header: t('staff.pilotStatus'),
        meta: { width: 130 },
        cell: ({ row }) =>
          row.original.pilot_status ? (
            <Badge
              size="sm"
              variant="light"
              color={row.original.pilot_status === 'STUDENT_PILOT' ? 'blue' : 'teal'}
            >
              {t(`staff.${row.original.pilot_status}`)}
            </Badge>
          ) : null,
      },
      {
        accessorKey: 'licence_codes',
        header: t('staff.licences'),
        meta: { width: 190 },
        cell: ({ row }) => (
          <Group gap={4} wrap="wrap">
            {row.original.licence_codes.map((code) => (
              <Badge key={code} size="xs" variant="outline">
                {code}
              </Badge>
            ))}
          </Group>
        ),
      },
      {
        id: 'staff_kinds',
        header: t('staff.title'),
        meta: { width: 190 },
        cell: ({ row }) => (
          <Text fz="xs" c="dimmed">
            {(row.original.staff_kinds ?? [])
              .map((value) => enumLabelFor(value))
              .filter(Boolean)
              .join(', ')}
          </Text>
        ),
      },
      {
        accessorKey: 'medical_valid_until',
        header: t('staff.medical'),
        meta: { width: 150 },
        cell: ({ row }) =>
          row.original.medical_valid_until ? (
            <Group gap={6} wrap="nowrap">
              <Text fz="sm" ff="monospace">
                {formatDate(row.original.medical_valid_until)}
              </Text>
              <StatusBadge
                kind="validity"
                value={row.original.medical_status}
                size="xs"
                variant="light"
              />
            </Group>
          ) : (
            <StatusBadge kind="validity" value="MISSING" size="xs" variant="light" />
          ),
      },
      {
        accessorKey: 'phone',
        header: t('staff.phone'),
        meta: { width: 140 },
        cell: ({ row }) => (
          <Text fz="sm" ff="monospace">
            {row.original.phone}
          </Text>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  function enumLabelFor(value: string) {
    return enumChoices('staff_kind').find((choice) => choice.value === value)?.label ?? value;
  }

  return (
    <>
      <PageHeader
        title={t('staff.title')}
        subtitle={organisation?.name}
        badge={people.data ? String(people.data.count) : undefined}
        actions={
          can('staff.change') ? (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setFormOpen(true)}>
              {t('staff.addPerson')}
            </Button>
          ) : null
        }
      />

      <Group mb="sm" gap="sm" wrap="wrap">
        <TextInput
          placeholder={t('action.search')}
          leftSection={<IconSearch size={15} />}
          value={query}
          onChange={(event) => {
            setQuery(event.currentTarget.value);
            setPage(1);
          }}
          w={220}
        />
        <Select
          placeholder={t('staff.pilotStatus')}
          data={enumChoices('pilot_status').map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          value={status}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
          clearable
          w={170}
        />
        <Select
          placeholder={t('staff.title')}
          data={enumChoices('staff_kind').map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
          value={kind}
          onChange={(value) => {
            setKind(value);
            setPage(1);
          }}
          clearable
          w={180}
        />
      </Group>

      <DataTable
        data={people.data?.results ?? []}
        columns={columns}
        isLoading={people.isLoading}
        onRowClick={(row) =>
          void navigate({
            to: '/$org/staff/$personId',
            params: { org, personId: String(row.id) },
            search: { tab: undefined },
          })
        }
        emptyTitle={t('staff.people')}
        minWidth={1000}
        pagination={{
          page,
          pageSize: 50,
          total: people.data?.count ?? 0,
          onPageChange: setPage,
        }}
      />

      <PersonFormDrawer opened={isFormOpen} onClose={() => setFormOpen(false)} />
    </>
  );
}

function initials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}
